// Shared helpers for the quiz PDF generation flow. The same `run` /
// `downloadFn` closures are produced for the initial addJob call site and for
// the PdfJobContext reload-recovery path, so behaviour stays in sync.

import { notifier } from 'components/Toaster';

const QUIZ_PDF_POLL_MS = 2000;
const QUIZ_PDF_POLL_TIMEOUT_MS = 45 * 60 * 1000;
const CHAPTER_PDF_POLL_MS = 4000;
const CHAPTER_PDF_POLL_TIMEOUT_MS = 60 * 60 * 1000;

export const getQuizPdfBasePath = ({ quizId }) => `/v1/admin/quiz/${quizId}/pdf`;
export const getChapterQuizPdfBasePath = (chapterId, category) =>
  `/v1/admin/chapters/${chapterId}/quiz-pdf/${category}`;

export const getQuizPdfFilename = (quizName = 'Quiz') =>
  `${String(quizName).replace(/\s+/g, '_')}.pdf`;

const getResponseData = (response = {}) => response?.data?.data || response?.data || {};
const wait = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

export function downloadPdfBlob(blobData, filename) {
  const blob =
    blobData instanceof Blob ? blobData : new Blob([blobData], { type: 'application/pdf' });
  const url = window.URL.createObjectURL(blob);
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = filename;
  document.body.appendChild(anchor);
  anchor.click();
  anchor.remove();
  setTimeout(() => window.URL.revokeObjectURL(url), 5000);
}

// ── Per-quiz PDF ────────────────────────────────────────────────────────────

async function pollQuizPdfGeneration({
  privateFetch,
  quizId,
  onProgress,
  isCancelled,
  cancelOnServer,
  signal,
}) {
  const startedAt = Date.now();
  while (true) {
    if (isCancelled()) {
      await cancelOnServer?.();
      throw new Error('PDF job cancelled');
    }
    if (Date.now() - startedAt > QUIZ_PDF_POLL_TIMEOUT_MS) {
      throw new Error('This is taking longer than expected. You can try downloading later.');
    }
    await wait(QUIZ_PDF_POLL_MS);
    if (isCancelled()) {
      await cancelOnServer?.();
      throw new Error('PDF job cancelled');
    }
    let statusData;
    try {
      const response = await privateFetch.get(`${getQuizPdfBasePath({ quizId })}/status`, {
        signal,
      });
      statusData = getResponseData(response);
    } catch (error) {
      if (error?.response?.status === 404)
        return { hasPdf: false, status: 'not_found', progress: 0 };
      // Transient errors (python app restart, gateway timeout) shouldn't kill
      // the job — keep polling, server may still be processing.
      continue;
    }
    const normalizedStatus = String(statusData?.status || '').toLowerCase();
    onProgress(Number(statusData?.progress || 0));
    if (normalizedStatus === 'completed') return statusData;
    if (normalizedStatus === 'failed' || normalizedStatus === 'not_found') {
      throw new Error('PDF generation failed. Please try again.');
    }
  }
}

// Builds a `run` closure suitable for PdfJobContext.addJob. `startMode`:
//   - 'start':  begin a new generation (PATCH /pdf, fallback POST on 404/405)
//   - 'attach': skip the start call (job is already running on the server)
function buildQuizPdfRun({ privateFetch, quizId, quizName, filename, startMode = 'start' }) {
  const safeName = (quizName || 'Quiz').trim() || 'Quiz';
  return async ({ updateJob, isCancelled, signal }) => {
    const quizPdfPath = getQuizPdfBasePath({ quizId });

    if (startMode === 'start') {
      updateJob({ status: 'SENDING', progress: 10, error: '' });
      try {
        await privateFetch.patch(quizPdfPath, { fileName: filename }, { signal });
      } catch (error) {
        if ([404, 405].includes(error?.response?.status)) {
          await privateFetch.post(quizPdfPath, { fileName: filename }, { signal });
        } else {
          throw error;
        }
      }
    }

    updateJob({ status: 'PROCESSING', progress: 30, error: '' });

    let serverCancelCalled = false;
    const cancelOnServer = async () => {
      if (serverCancelCalled) return;
      serverCancelCalled = true;
      try {
        await privateFetch.post(`${quizPdfPath}/cancel`);
      } catch (_) {
        // best-effort
      }
    };

    if (isCancelled()) {
      cancelOnServer();
      throw new Error('PDF job cancelled');
    }

    await pollQuizPdfGeneration({
      privateFetch,
      quizId,
      isCancelled,
      cancelOnServer,
      signal,
      onProgress: (progress) => {
        updateJob({
          status: 'PROCESSING',
          progress: Math.min(95, 30 + progress * 0.65),
        });
      },
    });

    if (isCancelled()) {
      cancelOnServer();
      throw new Error('PDF job cancelled');
    }

    updateJob({ status: 'COMPLETED', progress: 100, downloaded: false, error: '' });
    notifier.success(`${safeName} PDF generated and saved successfully.`);
  };
}

function buildQuizPdfDownloadFn({ privateFetch, quizId, quizName, filename }) {
  const safeName = (quizName || 'Quiz').trim() || 'Quiz';
  const safeFilename = filename || getQuizPdfFilename(safeName);
  return async () => {
    if (!quizId) return false;
    try {
      const response = await privateFetch.get(`${getQuizPdfBasePath({ quizId })}/download`, {
        responseType: 'blob',
      });
      downloadPdfBlob(response.data, safeFilename);
      notifier.success(`${safeName} PDF downloaded successfully.`);
      return true;
    } catch (error) {
      if (error?.response?.status === 404) {
        notifier.error('No PDF found. Please generate a PDF first.');
      } else {
        notifier.error('Failed to download PDF. Please try again.');
      }
      return false;
    }
  };
}

export function buildQuizPdfJobSpec({ privateFetch, quizId, quizName, filename, startMode }) {
  const safeName = (quizName || 'Quiz').trim() || 'Quiz';
  const safeFilename = filename || getQuizPdfFilename(safeName);
  return {
    title: `Quiz: ${safeName}`,
    filename: safeFilename,
    run: buildQuizPdfRun({
      privateFetch,
      quizId,
      quizName: safeName,
      filename: safeFilename,
      startMode,
    }),
    downloadFn: buildQuizPdfDownloadFn({
      privateFetch,
      quizId,
      quizName: safeName,
      filename: safeFilename,
    }),
    recoveryHint: { kind: 'quiz', quizId, quizName: safeName, filename: safeFilename },
  };
}

// ── Chapter-wide quiz PDF ───────────────────────────────────────────────────

function buildChapterQuizPdfRun({
  privateFetch,
  chapterId,
  category,
  chapterName,
  filename,
  startMode = 'start',
}) {
  const safeName = (chapterName || 'Chapter').trim() || 'Chapter';
  const basePath = getChapterQuizPdfBasePath(chapterId, category);
  return async ({ updateJob, isCancelled, signal }) => {
    if (startMode === 'start') {
      updateJob({ status: 'SENDING', progress: 10, error: '' });
      await privateFetch.post(basePath, { fileName: filename }, { signal });
    }
    updateJob({ status: 'PROCESSING', progress: 30, error: '' });

    const startedAt = Date.now();
    while (true) {
      if (isCancelled()) throw new Error('PDF job cancelled');
      if (Date.now() - startedAt > CHAPTER_PDF_POLL_TIMEOUT_MS)
        throw new Error('This is taking longer than expected. You can try downloading later.');
      await wait(CHAPTER_PDF_POLL_MS);
      if (isCancelled()) throw new Error('PDF job cancelled');

      let statusData;
      try {
        const res = await privateFetch.get(`${basePath}/status`, { signal });
        statusData = getResponseData(res);
      } catch (err) {
        if (err?.response?.status === 404)
          throw new Error('PDF generation failed. Please try again.');
        continue;
      }
      const s = String(statusData?.status || '').toLowerCase();
      updateJob({
        status: 'PROCESSING',
        progress: Math.min(95, 30 + Number(statusData?.progress || 0) * 0.65),
      });
      if (s === 'completed') break;
      if (s === 'failed' || s === 'not_found')
        throw new Error('PDF generation failed. Please try again.');
    }

    if (isCancelled()) throw new Error('PDF job cancelled');
    updateJob({ status: 'COMPLETED', progress: 100, downloaded: false, error: '' });
    notifier.success(`${safeName} combined PDF generated and saved successfully.`);
  };
}

function buildChapterQuizPdfDownloadFn({
  privateFetch,
  chapterId,
  category,
  chapterName,
  filename,
}) {
  const safeName = (chapterName || 'Chapter').trim() || 'Chapter';
  const safeFilename = filename || getQuizPdfFilename(`${safeName}_${category}`);
  return async () => {
    if (!chapterId) return false;
    try {
      const response = await privateFetch.get(
        `${getChapterQuizPdfBasePath(chapterId, category)}/download`,
        { responseType: 'blob' }
      );
      downloadPdfBlob(response.data, safeFilename);
      notifier.success(`${safeName} combined PDF downloaded successfully.`);
      return true;
    } catch (error) {
      if (error?.response?.status === 404)
        notifier.error('No PDF found. Please generate a PDF first.');
      else notifier.error('Failed to download PDF. Please try again.');
      return false;
    }
  };
}

export function buildChapterQuizPdfJobSpec({
  privateFetch,
  chapterId,
  category,
  chapterName,
  activeTab,
  filename,
  startMode,
}) {
  const safeName = (chapterName || 'Chapter').trim() || 'Chapter';
  const safeFilename = filename || getQuizPdfFilename(`${safeName}_${category}`);
  const titleSuffix =
    activeTab === 'qb' ? 'Q.Bank' : activeTab === 'ncert' ? 'NCERT' : 'Past Papers';
  return {
    title: `${safeName} — All ${titleSuffix} PDFs`,
    filename: safeFilename,
    run: buildChapterQuizPdfRun({
      privateFetch,
      chapterId,
      category,
      chapterName: safeName,
      filename: safeFilename,
      startMode,
    }),
    downloadFn: buildChapterQuizPdfDownloadFn({
      privateFetch,
      chapterId,
      category,
      chapterName: safeName,
      filename: safeFilename,
    }),
    recoveryHint: {
      kind: 'chapter-quiz',
      chapterId,
      category,
      chapterName: safeName,
      activeTab,
      filename: safeFilename,
    },
  };
}
